import { Toast } from 'primereact/toast';
import { useAtom } from 'jotai/index';
import { toastAtom } from '~/atoms/toastAtom';
import { Button } from 'primereact/button';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dropdown } from 'primereact/dropdown';
import { endpointJurisdictionsGET } from '~/api/def/endpoints/jurisdictionEndpoint';
import { currencyList } from '~/config/currenciesStatic';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { InputNumber } from 'primereact/inputnumber';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  endpointFundPATCH,
  FundGetData,
  FundPatchPayload,
} from '~/api/def/endpoints/fundEndpoint';
import { InputText } from 'primereact/inputtext';
import { useCallback, useEffect } from 'react';

export const FundSettingValidationSchema = z.object({
  name: z.string().nonempty('Name is required'),
  jurisdiction: z.string().nonempty('Jurisdiction is required'),
  size: z
    .number({
      required_error: 'Size is required',
      invalid_type_error: 'Size is required',
    })
    .nonnegative('Size must be a non-negative number'),
  currency: z.string().nonempty('Currency is required'),
  inceptionDate: z
    .date({
      required_error: 'Inception Date is required',
      invalid_type_error: 'Inception Date is required',
    })
    .refine((date) => date <= new Date(), {
      message: 'Inception date cannot be in the future',
    }),
  terminationDate: z
    .date()
    .refine((date) => date && date > new Date(), {
      message: 'Termination date must be in the future',
    })
    .optional()
    .nullable(),
  pCompanySetupCost: z
    .number()
    .nonnegative('Setup cost must be a non-negative number')
    .optional()
    .nullable(),
  investmentCostRate: z
    .number()
    .min(0, { message: 'Investment cost rate cannot be negative' })
    .max(100, { message: 'Investment cost rate cannot exceed 100' })
    .optional()
    .nullable(),
  managementFees: z
    .number({
      required_error: 'Management fees is required',
      invalid_type_error: 'Management fees is required',
    })
    .min(1, { message: 'Management fees cannot be negative' })
    .max(100, { message: 'Management fees cannot exceed 100' }),
  carrySetupDefault: z
    .number({
      required_error: 'Carry setup is required',
      invalid_type_error: 'Carry setup is required',
    })
    .min(0, { message: 'Carry setup cannot be negative' })
    .max(100, { message: 'Carry setup cannot exceed 100' }),
  carrySetupThresholds: z
    .array(
      z.object({
        condition: z.enum(['<', 'default']),
        value: z
          .number({
            required_error: 'Value is required',
            invalid_type_error: 'Value is required',
          })
          .min(0, { message: 'Value must be a non-negative number' })
          .max(100, { message: 'Value cannot exceed 100' }),
        percentage: z
          .number({
            required_error: 'Percentage is required',
            invalid_type_error: 'Percentage is required',
          })
          .min(0, { message: 'Percentage cannot be negative' })
          .max(100, { message: 'Percentage cannot exceed 100' }),
      }),
    )
    .optional(),
});

type ValidationSchema = z.infer<typeof FundSettingValidationSchema>;

interface FundCostFormProps {
  selectedFund: FundGetData;
  refetchFundListAnalytics: () => void;
  onHide: () => void;
}
export const FundCostForm = ({
  selectedFund,
  refetchFundListAnalytics,
  onHide,
}: FundCostFormProps) => {
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const { data: jurisdictionsData, isLoading: isJurisdictionsLoading } =
    useQuery({
      queryKey: ['portfolioJurisdictionsGet'],
      queryFn: () => endpointJurisdictionsGET(),
    });

  const getDefaultValues = useCallback(
    () => ({
      name: selectedFund.name,
      jurisdiction: selectedFund.jurisdiction.id,
      size: selectedFund.size,
      currency: selectedFund.currency,
      inceptionDate: selectedFund.inceptionDate
        ? new Date(selectedFund.inceptionDate)
        : undefined,
      terminationDate: selectedFund.terminationDate
        ? new Date(selectedFund.terminationDate)
        : undefined,
      pCompanySetupCost: selectedFund.companySetupCost || undefined,
      investmentCostRate: selectedFund.investmentCostRate,
      managementFees: selectedFund.managementFees || undefined,
      carrySetupDefault:
        (selectedFund.carrySetupThresholds &&
          selectedFund.carrySetupThresholds?.find(
            (threshold) => threshold.condition === 'default',
          )?.percentage) ||
        undefined,
      carrySetupThresholds:
        selectedFund.carrySetupThresholds &&
        selectedFund.carrySetupThresholds
          ?.filter(
            (threshold) =>
              threshold.condition !== 'default' && threshold.value !== null,
          )
          ?.map((threshold) => ({
            ...threshold,
            value: threshold.value ?? undefined,
          })),
    }),
    [selectedFund],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(FundSettingValidationSchema),
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    reset(getDefaultValues());
  }, [getDefaultValues, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'carrySetupThresholds',
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: FundPatchPayload) => {
      return endpointFundPATCH(selectedFund.id, data);
    },
    onSuccess: () => {
      toastCurrent?.show({
        severity: 'success',
        detail: 'Fund config updated successfully.',
      });
      refetchFundListAnalytics();
    },
    onError: () => {
      toastCurrent?.show({
        severity: 'error',
        detail: 'Something went wrong while updating fund config.',
      });
    },
    onSettled: () => {
      onHide();
      reset();
    },
  });

  const onSubmit = async (data: ValidationSchema) => {
    data.carrySetupThresholds?.push({
      condition: 'default',
      value: data.carrySetupDefault,
      percentage: data.carrySetupDefault,
    });
    const updatedValues: FundPatchPayload = {};
    updatedValues.name =
      data.name === selectedFund.name ? undefined : data.name;
    updatedValues.jurisdictionId =
      data.jurisdiction === selectedFund.jurisdiction.id
        ? undefined
        : data.jurisdiction;
    updatedValues.size =
      data.size === selectedFund.size ? undefined : data.size;
    updatedValues.currency =
      data.currency === selectedFund.currency ? undefined : data.currency;
    updatedValues.inceptionDate =
      data.inceptionDate.getTime() ===
      new Date(selectedFund.inceptionDate).getTime()
        ? undefined
        : new Date(data.inceptionDate);
    updatedValues.terminationDate =
      data.terminationDate &&
      data.terminationDate.getTime() ===
        new Date(selectedFund.terminationDate).getTime()
        ? undefined
        : data.terminationDate
          ? new Date(data.terminationDate)
          : undefined;
    updatedValues.companySetupCost =
      data.pCompanySetupCost === selectedFund.companySetupCost
        ? undefined
        : data.pCompanySetupCost;
    updatedValues.investmentCostRate =
      data.investmentCostRate === selectedFund.investmentCostRate
        ? undefined
        : data.investmentCostRate;
    updatedValues.managementFees =
      data.managementFees === selectedFund.managementFees
        ? undefined
        : data.managementFees;
    updatedValues.carrySetupThresholds =
      JSON.stringify(data.carrySetupThresholds) ===
      JSON.stringify(selectedFund.carrySetupThresholds)
        ? []
        : data.carrySetupThresholds || [];

    // Removing undefined and null values from changedValues
    Object.keys(updatedValues).forEach(
      (key) =>
        updatedValues[key as keyof FundPatchPayload] === undefined &&
        delete updatedValues[key as keyof FundPatchPayload],
    );
    await mutateAsync(updatedValues);
  };

  return (
    <form>
      <div className="flex p-3">
        <div className="grid w-6 align-content-start">
          <div className="col-12 flex flex-column gap-1">
            <label htmlFor="name">Name</label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => <InputText id="name" {...field} />}
            />
            {errors.name && (
              <small className="p-error">{errors.name.message}</small>
            )}
          </div>
          <div className="col-12 flex flex-column gap-1">
            <label htmlFor="jurisdiction">Jurisdiction</label>
            <Controller
              name="jurisdiction"
              control={control}
              render={({ field }) => (
                <Dropdown
                  id="type"
                  loading={isJurisdictionsLoading}
                  filter
                  {...field}
                  options={jurisdictionsData?.data.map(
                    (jurisdiction: { name: string; id: string }) => ({
                      label: jurisdiction.name,
                      value: jurisdiction.id,
                    }),
                  )}
                />
              )}
            />
            {errors.jurisdiction && (
              <small className="p-error">{errors.jurisdiction.message}</small>
            )}
          </div>
          <div className="col-8 flex flex-column gap-1">
            <label htmlFor="size">Size</label>
            <Controller
              name="size"
              control={control}
              render={({ field }) => (
                <InputNumber
                  {...field}
                  id="size"
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
            {errors.size && (
              <small className="p-error">{errors.size.message}</small>
            )}
          </div>
          <div className="col-4 flex flex-column gap-1">
            <label htmlFor="currency">Currency</label>
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <Dropdown
                  id="currency"
                  filter
                  {...field}
                  options={currencyList}
                  pt={{
                    panel: {
                      style: { width: '2rem' },
                    },
                  }}
                />
              )}
            />
            {errors.currency && (
              <small className="p-error">{errors.currency.message}</small>
            )}
          </div>
          <div className="col-6 flex flex-column gap-1">
            <label htmlFor="inceptionDate">Inception Date</label>
            <Controller
              name="inceptionDate"
              control={control}
              render={({ field }) => (
                <Calendar {...field} id="inceptionDate" showIcon />
              )}
            />
            {errors.inceptionDate && (
              <small className="p-error">{errors.inceptionDate.message}</small>
            )}
          </div>
          <div className="col-6 flex flex-column gap-1">
            <label htmlFor="terminationDate">Termination Date</label>
            <Controller
              name="terminationDate"
              control={control}
              render={({ field }) => (
                <Calendar {...field} id="terminationDate" showIcon />
              )}
            />
            {errors.terminationDate && (
              <small className="p-error">
                {errors.terminationDate.message}
              </small>
            )}
          </div>
        </div>
        <Divider layout="vertical" />
        <div className="grid w-6 align-content-start">
          <div className="col-12 flex flex-column gap-1">
            <label htmlFor="pCompanySetupCost">
              Portfolio Company Cost Setup
            </label>
            <Controller
              name="pCompanySetupCost"
              control={control}
              render={({ field }) => (
                <InputNumber
                  {...field}
                  id="pCompanySetupCost"
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
            {errors.pCompanySetupCost && (
              <small className="p-error">
                {errors.pCompanySetupCost.message}
              </small>
            )}
          </div>
          <div className="col-6 flex flex-column gap-1">
            <label htmlFor="investmentCostRate">Investment Cost Rate</label>
            <Controller
              name="investmentCostRate"
              control={control}
              render={({ field }) => (
                <div className="p-inputgroup">
                  <InputNumber
                    {...field}
                    id="investmentCostRate"
                    onChange={(e) => field.onChange(e.value)}
                  />
                  <span className="p-inputgroup-addon">%</span>
                </div>
              )}
            />
            {errors.investmentCostRate && (
              <small className="p-error">
                {errors.investmentCostRate.message}
              </small>
            )}
          </div>
          <div className="col-6 flex flex-column gap-1">
            <label htmlFor="managementFees">Management Fees</label>
            <Controller
              name="managementFees"
              control={control}
              render={({ field }) => (
                <div className="p-inputgroup">
                  <InputNumber
                    {...field}
                    id="managementFees"
                    onChange={(e) => field.onChange(e.value)}
                  />
                  <span className="p-inputgroup-addon">%</span>
                </div>
              )}
            />
            {errors.managementFees && (
              <small className="p-error">{errors.managementFees.message}</small>
            )}
          </div>
          <div className="w-full flex flex-column">
            <div className="flex">
              <div className="col-6 flex flex-column gap-1">
                <label htmlFor="carrySetupDefault">Carry Setup (TVPI)</label>
                <Controller
                  name="carrySetupDefault"
                  control={control}
                  render={({ field }) => (
                    <div className="p-inputgroup">
                      <InputNumber
                        {...field}
                        id="carrySetupDefault"
                        onChange={(e) => field.onChange(e.value)}
                      />
                      <span className="p-inputgroup-addon">%</span>
                    </div>
                  )}
                />
              </div>
              <div className="col-6 flex mt-auto flex-column gap-1">
                <Button
                  label="Add carry hurdle"
                  size="small"
                  icon="pi pi-plus"
                  className="w-10rem"
                  type="button"
                  onClick={() =>
                    append({ condition: '<', value: 0, percentage: 0 })
                  }
                />
              </div>
            </div>
            {errors.carrySetupDefault && (
              <small className="p-error ml-2">
                {errors.carrySetupDefault.message}
              </small>
            )}
          </div>
          {fields.map((_, index) => (
            <div key={index}>
              <div className="col-12 flex justify-content-between">
                <div className="p-inputgroup w-10">
                  <Controller
                    name={`carrySetupThresholds.${index}.value`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <span className="p-inputgroup-addon"> &lt; </span>
                        <InputNumber
                          {...field}
                          placeholder="value"
                          id={`carrySetupThresholds.${index}.value`}
                          onChange={(e) => field.onChange(e.value)}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name={`carrySetupThresholds.${index}.percentage`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <span className="p-inputgroup-addon">Then</span>
                        <InputNumber
                          {...field}
                          placeholder="Percentage"
                          id={`carrySetupThresholds.${index}.percentage`}
                          onChange={(e) => field.onChange(e.value)}
                        />
                        <span className="p-inputgroup-addon">%</span>
                      </>
                    )}
                  />
                </div>
                <div className="flex align-items-center">
                  <Button
                    label="Remove"
                    type="button"
                    severity="warning"
                    size="small"
                    style={{ height: '28px' }}
                    onClick={() => remove(index)}
                  />
                </div>
              </div>
              <div className="ml-2">
                {errors.carrySetupThresholds &&
                  errors.carrySetupThresholds[index]?.value && (
                    <small className="p-error">
                      {errors.carrySetupThresholds[index]?.value.message}
                    </small>
                  )}
                {errors.carrySetupThresholds &&
                  errors.carrySetupThresholds[index]?.percentage && (
                    <small className="p-error">
                      {errors.carrySetupThresholds &&
                      errors.carrySetupThresholds[index]?.value
                        ? ', '
                        : ''}
                      {errors.carrySetupThresholds[index]?.percentage.message}
                    </small>
                  )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className="flex justify-content-end p-3 gap-2"
        style={{
          borderTop: '1px solid #e9ecef',
        }}
      >
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={() => onHide()}
          className="p-button-text"
        />
        <Button
          type="submit"
          label="Save"
          icon={isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          disabled={isPending}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </form>
  );
};
